import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, Zap, Users, ArrowRight, CheckCircle, BarChart3, Globe, Lock, Search, TrendingUp, Briefcase, Cloud, Server, Phone, Star, Quote, LogOut, LayoutDashboard, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { LeadCapture, ExitIntentPopup } from '../components/LeadCapture';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';

const FAQS = [
  { q: 'Do you lock us into long contracts?', a: 'No. All plans are month-to-month. Cancel any time from your account. If we haven\'t delivered measurable value in 90 days, we refund the difference.' },
  { q: 'How does the marketplace work with your freelancers?', a: 'When you open a service request, we match you with vetted freelancers by skill, location, and availability. You approve the match, payment is held in escrow, and funds release when work is complete. 30% platform fee covers matching, QA, and dispute resolution.' },
  { q: 'Is our data really safe?', a: 'Encrypted in transit (TLS 1.2+) and at rest. Passwords are bcrypt-hashed. Session tokens live in httpOnly cookies (XSS-resistant). Payments go through Stripe — we never see card data. SOC 2 readiness on the roadmap for 2026.' },
  { q: 'Can we run this on our own infrastructure?', a: 'Yes — white-label and on-prem deployments are available on Pro plans. Contact sales if you need air-gapped or region-locked hosting.' },
  { q: 'Do you work with cannabis operators?', a: 'Yes. We have a dedicated cannabis compliance module with METRC integration. The team has years of experience with state-specific seed-to-sale reporting.' },
];

const FEATURES = [
  { icon: Shield, title: 'Hardened Perimeter', desc: 'Role-bound access, immutable audit trails, tenant isolation, and encrypted signal — engineered into the keel, not bolted on.' },
  { icon: Zap, title: 'Autonomous Dispatch', desc: 'GPT-5.2 triage routes operators by skill, geo, and live availability. SLA telemetry watches every ticket from intake to resolution.' },
  { icon: Users, title: 'Hybrid Operator Mesh', desc: 'Internal teams and vetted external contractors orchestrated through one command surface.' },
  { icon: Search, title: 'Digital Reconnaissance', desc: 'AI sweeps your digital presence — missing footprints, weak signals, and untapped growth vectors surfaced in minutes.' },
  { icon: Globe, title: 'Web Surface Synthesis', desc: 'Spin up industry-tuned web presences in seconds. Ship the storefront, hold the runway.' },
  { icon: Briefcase, title: 'Sovereign Referral Network', desc: 'A trust-graph of vetted partners for design, SEO, cyber, branding, and managed IT — bonded by escrow, not handshakes.' },
  { icon: BarChart3, title: 'Full-Spectrum Visibility', desc: 'Every ticket, project, dispatch, and SLA breach — captured in a single live console with cryptographic audit lineage.' },
  { icon: TrendingUp, title: 'Growth Telemetry', desc: 'Diagnose, recommend, automate, refer. Operations becomes a flywheel — every signal compounding into revenue.' },
  { icon: Lock, title: 'Cyber Combat-Ready', desc: 'Incident playbooks, compliance maps, and threat hunts purpose-built for the modern adversary surface.' },
];

const PLANS = [
  { name: 'Starter', price: '$29', period: '/mo', features: ['Basic ticketing', 'In-app chat', 'Up to 5 team members', 'Standard reporting', 'Email support'], cta: 'Start Free Trial' },
  { name: 'Growth', price: '$79', period: '/mo', featured: true, features: ['Everything in Starter', 'Marketplace access', 'Hybrid routing', 'SLA management', 'Project tracking', 'Advanced workflows'], cta: 'Start Free Trial' },
  { name: 'Pro', price: '$199', period: '/mo', features: ['Everything in Growth', 'Multi-location support', 'Advanced dispatch', 'Compliance & audit', 'Custom workflows', 'Priority support'], cta: 'Contact Sales' },
];

export default function Landing() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/api/marketing/testimonials');
        setTestimonials(data.testimonials || []);
      } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const onLogout = async () => {
    setMenuOpen(false);
    await logout();
    navigate('/');
  };

  const initials = (user?.name || user?.email || '?')
    .split(/[@\s]/)[0]
    .split(/[.\s]/)
    .map(w => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="glass-header sticky top-0 z-50 px-6 md:px-12 lg:px-24" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
            <div className="w-8 h-8 bg-[#D4A843] rounded-lg flex items-center justify-center">
              <Shield className="w-4 h-4 text-[#0A0A0A]" strokeWidth={2} />
            </div>
            <span className="font-heading text-xl font-bold tracking-tight text-gray-900">TechOps</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/services" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">Services</Link>
            <a href="#features" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">Features</a>
            <Link to="/pricing" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">Pricing</Link>
            <a href="#migration" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">Cloud Migration</a>
          </div>
          <div className="flex items-center gap-3">
            {user ? (
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setMenuOpen((o) => !o)}
                  className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg hover:bg-gray-100 transition-colors"
                  data-testid="nav-account-menu"
                >
                  <span className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D4A843] to-[#B8923A] text-[#0A0A0A] text-xs font-bold flex items-center justify-center">{initials}</span>
                  <ChevronDown className="w-3 h-3 text-gray-500" />
                </button>
                {menuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden" data-testid="nav-account-dropdown">
                    <div className="p-3 border-b border-gray-100">
                      <p className="text-sm font-semibold text-gray-900 truncate">{user.name || user.email}</p>
                      {user.name && <p className="text-[11px] text-gray-500 truncate">{user.email}</p>}
                      <p className="text-[10px] text-[#8A6D1E] uppercase tracking-wide mt-1">{(user.role || '').replace(/_/g, ' ')}</p>
                    </div>
                    <Link to="/dashboard" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 transition-colors" data-testid="nav-menu-dashboard">
                      <LayoutDashboard className="w-3.5 h-3.5" /> Dashboard
                    </Link>
                    <button
                      onClick={onLogout}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50 transition-colors border-t border-gray-100"
                      data-testid="nav-logout"
                    >
                      <LogOut className="w-3.5 h-3.5" /> Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login-btn">
                  <Button variant="ghost" className="text-sm font-medium">Sign In</Button>
                </Link>
                <Link to="/register" data-testid="nav-register-btn">
                  <Button className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] text-sm px-5 rounded-lg font-semibold">Get Started</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="hero-gradient relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)', backgroundSize: '40px 40px'}} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-24 lg:py-36 sovereign-halo">
          <div className="max-w-3xl">
            <p className="text-[0px] leading-none opacity-0 absolute" aria-hidden="true">Impact-Site-Verification: 1bbe7119-5c70-4120-875a-d0682cb02a9b</p>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/15 text-white/80 text-[10px] font-display mb-6 backdrop-blur-sm" data-testid="hero-eyebrow">
              <span className="signal-dot" />
              <span>Sovereign Ops · Live</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tighter text-white leading-[0.95] mb-6" data-testid="hero-heading">
              Command Your <span className="text-sovereign">Digital Hull.</span><br/>
              Operate at <span className="text-sovereign">Sovereign</span> Scale.
            </h1>
            <p className="text-lg text-gray-300/90 leading-relaxed max-w-xl mb-10">
              A unified flight deck for IT operations, cyber defense, dispatch, growth telemetry, and the freelance network that powers it. Internal teams and external operators — synchronized on one signal.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/register" data-testid="hero-cta-btn">
                <Button size="lg" className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] px-8 py-3 text-base rounded-lg font-semibold w-full sm:w-auto rim-gold">
                  Begin Free Trial <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/services" data-testid="hero-services-btn">
                <Button size="lg" variant="outline" className="border-white/25 text-white hover:bg-white/10 hover:border-[#00E5FF]/60 px-8 py-3 text-base rounded-lg w-full sm:w-auto">
                  Explore Capabilities
                </Button>
              </Link>
              <Link to="/join" data-testid="hero-join-btn">
                <Button size="lg" variant="outline" className="border-[#7C3AED]/50 text-[#C4A6FF] hover:bg-[#7C3AED]/10 px-8 py-3 text-base rounded-lg w-full sm:w-auto">
                  Enlist in the Network
                </Button>
              </Link>
              <Link to="/diagnostics" data-testid="hero-diagnostics-btn">
                <Button size="lg" className="bg-transparent text-[#00E5FF] hover:bg-[#00E5FF]/10 px-8 py-3 text-base rounded-lg w-full sm:w-auto font-semibold rim-cyan">
                  Free Device Telemetry Scan
                </Button>
              </Link>
            </div>
            {/* Lead Magnet */}
            <div className="mt-8">
              <LeadCapture magnet="security_assessment" page="landing" title="Get a Free IT Security Assessment" subtitle="Enter your email — we'll analyze your infrastructure and send a personalized security report." variant="hero" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="border-b border-gray-200 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[{ n: '99.9%', l: 'Uptime SLA' }, { n: '< 15min', l: 'Avg Response' }, { n: '500+', l: 'Technicians' }, { n: '10k+', l: 'Tickets Resolved' }].map((s, i) => (
            <div key={`item-${i}`} className="text-center">
              <p className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-gray-900">{s.n}</p>
              <p className="text-sm text-gray-500 mt-1">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 lg:py-32 bg-white relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative">
          <div className="max-w-2xl mb-16">
            <p className="font-display text-[10px] tracking-[0.32em] text-[#D4A843] mb-3 flex items-center gap-2">
              <span className="signal-dot gold" /> Capabilities
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-gray-900 mb-4 leading-[1.05]">
              Engineered for operators who don't <span className="text-sovereign">wait for permission.</span>
            </h2>
            <p className="text-base text-gray-500 leading-relaxed">
              From consumer telemetry to enterprise cyber response — TechOps shapes itself to your flight pattern.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-stagger" data-testid="features-grid">
            {FEATURES.map((f) => (
              <div key={f.title} className="hover-lift bg-white border border-gray-200 rounded-xl p-6 md:p-8 group">
                <div className="w-11 h-11 rounded-lg bg-[#D4A843]/10 flex items-center justify-center mb-5 group-hover:bg-[#D4A843]/20 transition-colors">
                  <f.icon className="w-5 h-5 text-[#D4A843] group-hover:scale-110 transition-transform" strokeWidth={1.6} />
                </div>
                <h3 className="font-heading text-lg font-extrabold text-gray-900 mb-2 tracking-tight">{f.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 lg:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="uppercase text-xs tracking-[0.2em] font-medium text-[#D4A843] mb-3">Pricing</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-gray-900 mb-4">
              Plans That Scale With You
            </h2>
            <p className="text-base text-gray-500">Start free. Upgrade when you need marketplace access, advanced workflows, or enterprise features.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto" data-testid="pricing-grid">
            {PLANS.map((plan, i) => (
              <div key={`item-${i}`} className={`pricing-card bg-white rounded-lg p-8 ${plan.featured ? 'featured' : ''}`}>
                {plan.featured && (
                  <span className="inline-block text-xs font-semibold tracking-wide uppercase bg-[#D4A843] text-[#0A0A0A] font-semibold px-3 py-1 rounded mb-4">Most Popular</span>
                )}
                <h3 className="font-heading text-xl font-bold text-gray-900">{plan.name}</h3>
                <div className="flex items-baseline gap-1 mt-4 mb-6">
                  <span className="font-heading text-4xl font-bold tracking-tight text-gray-900">{plan.price}</span>
                  <span className="text-sm text-gray-500">{plan.period}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((f, j) => (
                    <li key={`j-${j}`} className="flex items-start gap-2.5 text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" strokeWidth={2} />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link to="/register" data-testid={`pricing-${plan.name.toLowerCase()}-btn`}>
                  <Button
                    className={`w-full rounded-lg ${plan.featured ? 'bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold' : 'bg-white text-gray-900 border border-gray-300 hover:bg-gray-50'}`}
                  >
                    {plan.cta}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cloud Migration Services */}
      <section id="migration" className="py-20 lg:py-32 bg-white" data-testid="migration-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="uppercase text-xs tracking-[0.2em] font-medium text-[#D4A843] mb-3">Cloud Services</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-gray-900 mb-4">
              AI-Powered Cloud Migration
            </h2>
            <p className="text-base text-gray-500">We migrate, optimize, and future-proof your business with cloud technologies, AI automation, and on-demand IT support.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto" data-testid="migration-tiers">
            {[
              { name: 'Starter', price: '$2,500', sub: '- $5,000', features: ['Architecture assessment', 'Basic migration plan', 'DNS & networking setup', 'Email support'], ideal: 'Small businesses, 1-5 servers', time: '1-2 weeks' },
              { name: 'Professional', price: '$7,500', sub: '- $15,000', featured: true, features: ['Full architecture assessment', 'Migration strategy & execution', 'Data transfer & validation', 'DNS & networking setup', 'Performance optimization', '30-day post-migration monitoring'], ideal: 'Growing companies, 5-20 servers', time: '2-4 weeks' },
              { name: 'Enterprise', price: '$20,000', sub: '+', features: ['Full assessment & gap analysis', 'Custom migration strategy', 'Data transfer & compliance', 'Security hardening', 'Performance & auto-scaling', 'AI-powered team training', 'TechOps platform deployment', '90-day support'], ideal: 'Enterprise, 20+ servers', time: '4-12 weeks' },
            ].map((tier, i) => (
              <div key={`item-${i}`} className={`bg-white rounded-lg p-8 border-2 ${tier.featured ? 'border-[#D4A843] shadow-lg' : 'border-gray-200'}`}>
                {tier.featured && <span className="inline-block text-xs font-semibold tracking-wide uppercase bg-[#D4A843] text-[#0A0A0A] font-semibold px-3 py-1 rounded mb-4">Most Popular</span>}
                <div className="flex items-center gap-2 mb-2"><Cloud className="w-5 h-5 text-[#D4A843]" /><h3 className="font-heading text-xl font-bold text-gray-900">{tier.name}</h3></div>
                <div className="flex items-baseline gap-0.5 mt-3 mb-2">
                  <span className="font-heading text-3xl font-bold tracking-tight text-gray-900">{tier.price}</span>
                  <span className="text-sm text-gray-500">{tier.sub}</span>
                </div>
                <p className="text-xs text-gray-400 mb-4">{tier.ideal} | {tier.time}</p>
                <ul className="space-y-2 mb-6">
                  {tier.features.map((f, j) => (
                    <li key={`j-${j}`} className="flex items-start gap-2 text-sm text-gray-600"><CheckCircle className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />{f}</li>
                  ))}
                </ul>
                <Link to="/register" data-testid={`migration-${tier.name.toLowerCase()}-btn`}>
                  <Button className={`w-full rounded-lg ${tier.featured ? 'bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold' : 'bg-white text-gray-900 border border-gray-300 hover:bg-gray-50'}`}>
                    Get Started
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Book Assessment CTA */}
      <section className="py-16 bg-gradient-to-br from-[#0A0A0A] to-[#1A0A2E]" data-testid="assessment-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="max-w-xl">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-3">Book a Free IT Assessment</h2>
            <p className="text-base text-blue-200">Not sure which plan or service is right? Our AI-powered assessment analyzes your infrastructure and recommends the best path forward — completely free.</p>
          </div>
          <Link to="/register" data-testid="book-assessment-btn">
            <Button size="lg" className="bg-[#D4A843] text-[#0A0A0A] hover:bg-[#E8C972] px-8 py-3 text-base rounded-lg font-semibold shrink-0">
              <Phone className="w-4 h-4 mr-2" /> Book Free Assessment
            </Button>
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-4">
            Ready to Unify Your IT Operations?
          </h2>
          <p className="text-base text-gray-400 mb-8 max-w-xl mx-auto">
            Join businesses managing internal teams, contractors, and cyber workflows on one platform.
          </p>
          <Link to="/register" data-testid="cta-register-btn">
            <Button size="lg" className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold px-10 py-3 text-base rounded-lg">
              Get Started Free <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="py-20 bg-gradient-to-b from-white to-gray-50" data-testid="landing-testimonials">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <span className="inline-block text-[10px] font-bold px-3 py-1 rounded-full bg-[#D4A843]/15 text-[#8A6D1E] uppercase tracking-wider mb-3">Testimonials</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">What operators say</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.slice(0, 3).map((t) => (
                <div key={t.id} className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
                  <Quote className="w-5 h-5 text-[#D4A843] mb-3" />
                  <p className="text-sm text-gray-700 leading-relaxed mb-4">"{t.quote}"</p>
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating || 5 }).map((_, i) => <Star key={`s-${i}`} className="w-3 h-3 fill-[#D4A843] text-[#D4A843]" />)}
                  </div>
                  <div className="text-xs text-gray-500">
                    <p className="font-semibold text-gray-900">{t.author_name}</p>
                    {t.author_title && <p>{t.author_title}{t.author_company ? ` · ${t.author_company}` : ''}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="inline-block text-[10px] font-bold px-3 py-1 rounded-full bg-[#D4A843]/15 text-[#8A6D1E] uppercase tracking-wider mb-3">FAQ</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Honest answers to the questions most people ask</h2>
          </div>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <details key={`faq-${i}`} className="group bg-gray-50 border border-gray-200 rounded-xl overflow-hidden" data-testid={`landing-faq-${i}`}>
                <summary className="cursor-pointer p-5 font-semibold text-gray-900 flex items-center justify-between">
                  <span>{faq.q}</span>
                  <span className="text-[#D4A843] text-xl transition-transform group-open:rotate-45">+</span>
                </summary>
                <div className="px-5 pb-5 text-sm text-gray-600 leading-relaxed">{faq.a}</div>
              </details>
            ))}
          </div>
          <p className="text-center text-sm text-gray-500 mt-8">Still have questions? <Link to="/contact" className="text-[#D4A843] font-semibold hover:underline">Contact us →</Link></p>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-2xl mx-auto px-6">
          <LeadCapture magnet="cyber_checklist" page="landing_bottom" title="Download Our 25-Point Cybersecurity Checklist" subtitle="Free guide to securing your business — used by 500+ companies. No spam, just security." variant="card" />
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 py-8 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-[#D4A843] rounded flex items-center justify-center">
              <Shield className="w-3 h-3 text-white" strokeWidth={2} />
            </div>
            <span className="font-heading text-sm font-bold text-gray-900">TechOps</span>
          </div>
          <div className="flex gap-6 text-xs text-gray-500">
            <Link to="/about" className="hover:text-gray-900">About</Link>
            <Link to="/blog" className="hover:text-gray-900">Blog</Link>
            <Link to="/contact" className="hover:text-gray-900">Contact</Link>
            <Link to="/privacy" className="hover:text-gray-900">Privacy</Link>
            <Link to="/terms" className="hover:text-gray-900">Terms</Link>
          </div>
          <p className="text-xs text-gray-400">2026 EIGOO Inc | <a href="https://eigooinc.net" target="_blank" rel="noopener noreferrer" className="text-[#D4A843] hover:underline">eigooinc.net</a> | Powered by <a href="https://app.emergent.sh/?via=Eigoo" target="_blank" rel="noopener noreferrer" className="text-[#D4A843] hover:underline">Emergent</a> | <a href="https://www.skool.com/signup?ref=e3e2967f8a3846bb87a53db8b592c54e" target="_blank" rel="noopener noreferrer" className="text-[#D4A843] hover:underline">Join our Skool</a></p>
        </div>
      </footer>

      <ExitIntentPopup />
    </div>
  );
}
